import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import './LLMSettingsPage.css';
import AddLLMModal from './AddLLMModal';

const LLMSettingsPage = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [providers, setProviders] = useState([]);
    const [useSameModelForAll, setUseSameModelForAll] = useState(false);
    const [customModelsList, setCustomModelsList] = useState([]);
    const [showAddLLMModal, setShowAddLLMModal] = useState(false);
    const [loadingModels, setLoadingModels] = useState({}); // Track loading state per provider
    const [preferences, setPreferences] = useState(null); // Store preferences for key status
    
    const [formData, setFormData] = useState({
        // Per-agent providers
        extractor_provider: 'openai',
        evaluator_provider: 'openai',
        summarizer_provider: 'openai',
        // Legacy provider (for backward compatibility)
        provider: 'openai',
        api_key: '',
        extractor_model: 'gpt-4o-mini',
        evaluator_model: 'gpt-4o-mini',
        summarizer_model: 'gpt-4o-mini',
        extractor_temperature: 0.0,
        evaluator_temperature: 0.0,
        summarizer_temperature: 0.0,
        use_same_model_for_all: false,
        base_url: '',
        azure_endpoint: '',
        azure_api_version: '',
        azure_deployment_name: '',
        is_active: true
    });
    
    const [customModels, setCustomModels] = useState({
        extractor: '',
        evaluator: '',
        summarizer: ''
    });
    
    const navigate = useNavigate();
    
    useEffect(() => {
        fetchProviders();
        fetchSettings();
        fetchCustomModels();
        fetchPreferences();
    }, []);
    
    const fetchProviders = async () => {
        try {
            // backendUrl handled by apiFetch
            // Fetch only vendors that have been added (have CompanyLLMSettings records)
            const response = await apiFetch(`/api/llm-settings/added-vendors`, {
            });
            if (response.ok) {
                const data = await response.json();
                setProviders(data.vendors || []);
            } else {
                // If endpoint fails, fall back to empty list
                console.warn('Failed to fetch added vendors, using empty list');
                setProviders([]);
            }
        } catch (err) {
            console.error('Error fetching added vendors:', err);
            setProviders([]);
        }
    };
    
    const fetchCustomModels = async () => {
        try {
            // backendUrl handled by apiFetch
            const response = await apiFetch(`/api/llm-settings/custom-models`, {
            });
            if (response.ok) {
                const data = await response.json();
                setCustomModelsList(data.models || []);
            }
        } catch (err) {
            console.error('Error fetching custom models:', err);
        }
    };
    
    const fetchPreferences = async () => {
        try {
            // backendUrl handled by apiFetch
            const response = await apiFetch(`/api/llm-settings/preferences`, {
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setPreferences(data.preferences);
                }
            }
        } catch (err) {
            console.error('Error fetching preferences:', err);
        }
    };
    
    const handleAddCustomModel = async (modelData) => {
        // backendUrl handled by apiFetch
        const response = await apiFetch(`/api/llm-settings/custom-models`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(modelData)
        });
        
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(getUserFacingErrorMessage(response.status, data, 'Failed to add model'));
        }
        
        await fetchCustomModels(); // Refresh list
        await fetchProviders(); // Refresh providers to include new model
    };
    
    const fetchSettings = async () => {
        try {
            setLoading(true);
            // backendUrl handled by apiFetch
            const response = await apiFetch(`/api/llm-settings`, {
            });
            
            if (response.ok) {
                const data = await response.json();
                // Handle both cases: settings exists or settings is None (not configured yet)
                if (data.settings) {
                    setSettings(data.settings);
                    // Load per-agent providers, fallback to legacy provider
                    const extractorProvider = data.settings.extractor_provider || data.settings.provider || 'openai';
                    const evaluatorProvider = data.settings.evaluator_provider || data.settings.provider || 'openai';
                    const summarizerProvider = data.settings.summarizer_provider || data.settings.provider || 'openai';
                    
                    const useSameModel = data.settings.use_same_model_for_all || false;
                    setUseSameModelForAll(useSameModel);
                    
                    setFormData({
                        extractor_provider: extractorProvider,
                        evaluator_provider: evaluatorProvider,
                        summarizer_provider: summarizerProvider,
                        provider: data.settings.provider || 'openai', // Legacy
                        api_key: data.settings.api_key || '', // Will be masked
                        extractor_model: data.settings.extractor_model || 'gpt-4o-mini',
                        evaluator_model: data.settings.evaluator_model || 'gpt-4o-mini',
                        summarizer_model: data.settings.summarizer_model || 'gpt-4o-mini',
                        extractor_temperature: data.settings.extractor_temperature || 0.0,
                        evaluator_temperature: data.settings.evaluator_temperature || 0.0,
                        summarizer_temperature: data.settings.summarizer_temperature || 0.0,
                        use_same_model_for_all: useSameModel,
                        base_url: data.settings.base_url || '',
                        azure_endpoint: data.settings.azure_endpoint || '',
                        azure_api_version: data.settings.azure_api_version || '',
                        azure_deployment_name: data.settings.azure_deployment_name || '',
                        is_active: data.settings.is_active !== false
                    });
                } else {
                    // No settings configured yet - use defaults
                    setSettings(null);
                    // Initialize formData with defaults if not already set
                    const defaultProvider = providers.length > 0 ? providers[0].id : 'openai';
                    const defaultModel = providers.length > 0 ? (providers[0].models?.find(m => m !== 'custom') || 'gpt-4o-mini') : 'gpt-4o-mini';
                    if (!formData.extractor_provider) {
                        setFormData({
                            extractor_provider: defaultProvider,
                            evaluator_provider: defaultProvider,
                            summarizer_provider: defaultProvider,
                            provider: defaultProvider, // Legacy
                            api_key: '',
                            extractor_model: defaultModel,
                            evaluator_model: defaultModel,
                            summarizer_model: defaultModel,
                            extractor_temperature: 0.0,
                            evaluator_temperature: 0.0,
                            summarizer_temperature: 0.0,
                            use_same_model_for_all: false,
                            base_url: '',
                            azure_endpoint: '',
                            azure_api_version: '',
                            azure_deployment_name: '',
                            is_active: true
                        });
                    }
                }
            } else {
                if (response.status === 403) {
                    setError('You do not have permission to access LLM settings.');
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    setError(errorData.error || 'Failed to load settings.');
                }
            }
        } catch (err) {
            console.error('Error fetching settings:', err);
            setError('Failed to load settings.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleUseSameModelChange = (e) => {
        const checked = e.target.checked;
        setUseSameModelForAll(checked);
        setFormData(prev => ({
            ...prev,
            use_same_model_for_all: checked
        }));
        
        if (checked) {
            // Copy extractor settings to all agents
            setFormData(prev => ({
                ...prev,
                use_same_model_for_all: checked,
                evaluator_provider: prev.extractor_provider,
                evaluator_model: prev.extractor_model,
                evaluator_temperature: prev.extractor_temperature,
                summarizer_provider: prev.extractor_provider,
                summarizer_model: prev.extractor_model,
                summarizer_temperature: prev.extractor_temperature
            }));
        }
    };
    
    const handleInputChange = async (e) => {
        const { name, value, type, checked } = e.target;
        
        // Handle per-agent provider changes
        if (name === 'extractor_provider' || name === 'evaluator_provider' || name === 'summarizer_provider') {
            const agentName = name.replace('_provider', '');
            const modelField = `${agentName}_model`;
            
            // Set loading state for this provider
            setLoadingModels(prev => ({ ...prev, [value]: true }));
            
            // Fetch models dynamically for the selected provider
            let models = [];
            let defaultModel = 'gpt-4o-mini';
            
            try {
                // backendUrl handled by apiFetch
                const modelsResponse = await apiFetch(`/api/llm-settings/models?provider=${value}`, {
                });
                
                if (modelsResponse.ok) {
                    const modelsData = await modelsResponse.json();
                    if (modelsData.success && modelsData.models) {
                        // Extract model IDs from the response (handle both string and object formats)
                        models = modelsData.models.map(m => {
                            if (typeof m === 'string') return m;
                            return m.id || m.name || m.model_id || m;
                        });
                        // Set default to first non-custom model
                        defaultModel = models.find(m => m !== 'custom' && m) || models[0] || 'gpt-4o-mini';
                    }
                } else {
                    // If API fails, try to get error message
                    const errorData = await modelsResponse.json().catch(() => ({}));
                    console.warn(`Failed to fetch models for ${value}:`, errorData.error || 'Unknown error');
                }
            } catch (err) {
                console.error(`Error fetching models for ${value}:`, err);
            } finally {
                setLoadingModels(prev => ({ ...prev, [value]: false }));
            }
            
            // Fallback to provider's static models if fetch failed or returned empty
            if (models.length === 0) {
                const provider = providers.find(p => p.id === value);
                models = provider?.models || [];
                defaultModel = models.find(m => m !== 'custom') || 'gpt-4o-mini';
            }
            
            // Update providers array with fetched models for this provider
            setProviders(prev => {
                const updated = [...prev];
                const providerIndex = updated.findIndex(p => p.id === value);
                if (providerIndex >= 0 && models.length > 0) {
                    updated[providerIndex] = {
                        ...updated[providerIndex],
                        models: models
                    };
                } else if (providerIndex < 0 && models.length > 0) {
                    // Provider not in list, add it
                    updated.push({
                        id: value,
                        name: value.charAt(0).toUpperCase() + value.slice(1),
                        models: models
                    });
                }
                return updated;
            });
            
            const updates = {
                [name]: value,
                [modelField]: defaultModel,
                provider: name === 'extractor_provider' ? value : formData.provider
            };
            
            // If checkbox is checked and this is extractor, sync to all
            if (useSameModelForAll && name === 'extractor_provider') {
                updates.evaluator_provider = value;
                updates.evaluator_model = defaultModel;
                updates.summarizer_provider = value;
                updates.summarizer_model = defaultModel;
            }
            
            setFormData(prev => ({ ...prev, ...updates }));
            
            // Clear custom model for this agent
            setCustomModels(prev => ({
                ...prev,
                [agentName]: ''
            }));
        } else if (name.includes('_model') || name.includes('_temperature')) {
            const updates = { [name]: type === 'checkbox' ? checked : value };
            
            // If checkbox is checked and this is extractor, sync to all
            if (useSameModelForAll && name.startsWith('extractor_')) {
                const fieldSuffix = name.replace('extractor_', '');
                updates[`evaluator_${fieldSuffix}`] = type === 'checkbox' ? checked : value;
                updates[`summarizer_${fieldSuffix}`] = type === 'checkbox' ? checked : value;
            }
            
            setFormData(prev => ({ ...prev, ...updates }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };
    
    // Helper to get models for a specific agent's provider
    const getProviderModelsForAgent = (agentName) => {
        const providerField = `${agentName}_provider`;
        const providerId = formData[providerField] || 'openai';
        const provider = providers.find(p => p.id === providerId);
        const models = provider?.models || [];
        
        // Add custom models for this provider
        const providerCustomModels = customModelsList
            .filter(m => m.provider === providerId)
            .map(m => m.model_name);
        
        const allModels = [...models, ...providerCustomModels];
        
        // Add "custom" option if not present
        if (!allModels.includes('custom')) {
            return [...allModels, 'custom'];
        }
        return allModels;
    };
    
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            
            // backendUrl handled by apiFetch
            const response = await apiFetch(`/api/llm-settings`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
            
            const data = await response.json();
            
            if (data.success) {
                setSuccess('Settings saved successfully!');
                setSettings(data.settings);
                // Don't update api_key field if it was masked
                if (data.settings.api_key === '••••••••') {
                    setFormData(prev => ({ ...prev, api_key: '' }));
                }
                // Refresh preferences to get updated key status
                await fetchPreferences();
            } else {
                setError(data.error || 'Failed to save settings.');
            }
        } catch (err) {
            console.error('Error saving settings:', err);
            setError('Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };
    
    // Legacy function - kept for backward compatibility
    const getProviderModels = () => {
        return getProviderModelsForAgent('extractor');
    };
    
    const isCustomModel = (modelName) => {
        if (!modelName || modelName === 'custom') return false;
        const provider = providers.find(p => p.id === formData.provider);
        const defaultModels = provider?.models || [];
        // Remove 'custom' from the list for comparison
        const modelsWithoutCustom = defaultModels.filter(m => m !== 'custom');
        return !modelsWithoutCustom.includes(modelName);
    };
    
    const handleModelChange = (e, agentType) => {
        const value = e.target.value;
        
        // Update only the selected agent's model
        if (value === 'custom') {
            const updates = { [agentType]: 'custom' };
            // If checkbox is checked and this is extractor, sync to all
            if (useSameModelForAll && agentType === 'extractor_model') {
                updates.evaluator_model = 'custom';
                updates.summarizer_model = 'custom';
            }
            setFormData(prev => ({ ...prev, ...updates }));
            const fieldName = agentType.replace('_model', '');
            setCustomModels(prev => ({ ...prev, [fieldName]: '' }));
        } else {
            const updates = { [agentType]: value };
            // If checkbox is checked and this is extractor, sync to all
            if (useSameModelForAll && agentType === 'extractor_model') {
                updates.evaluator_model = value;
                updates.summarizer_model = value;
            }
            setFormData(prev => ({ ...prev, ...updates }));
            const fieldName = agentType.replace('_model', '');
            setCustomModels(prev => ({ ...prev, [fieldName]: '' }));
        }
    };
    
    const handleCustomModelInput = (e, agentType) => {
        const value = e.target.value;
        const fieldName = agentType.replace('_model', '');
        setCustomModels(prev => ({ ...prev, [fieldName]: value }));
        setFormData(prev => ({ ...prev, [agentType]: value }));
    };
    
    const getProviderInfo = () => {
        return providers.find(p => p.id === formData.provider);
    };
    
    if (loading) {
        return (
            <div className="llm-settings-content">
                <div className="loading">Loading settings...</div>
            </div>
        );
    }
    
    return (
        <div className="llm-settings-content">
            {error && (
                <div className="alert alert-error">
                    {error}
                </div>
            )}
            
            {success && (
                <div className="alert alert-success">
                    {success}
                </div>
            )}
                
                {/* Current Configuration Display - Read-only Information */}
                <div className="current-config-section">
                    <div className="config-info-card config-readonly">
                        <div className="config-card-header-actions">
                            <button 
                                onClick={() => setShowAddLLMModal(true)} 
                                className="add-llm-btn"
                            >
                                + Add LLM
                            </button>
                        </div>
                        <h2 className="config-title config-title-readonly">
                            <span className="config-icon">ℹ️</span>
                            Current Configuration
                            <span className="config-badge">Read-only</span>
                        </h2>
                        {/* Agent Configuration Table */}
                        <div className="config-table">
                            <div className="config-table-header">
                                <div className="config-table-cell">Agent</div>
                                <div className="config-table-cell">Vendor</div>
                                <div className="config-table-cell">Model</div>
                            </div>
                            <div className="config-table-row">
                                <div className="config-table-cell config-agent-name">Extractor Agent</div>
                                <div className="config-table-cell">{settings?.extractor_provider || formData.extractor_provider || 'Not configured'}</div>
                                <div className="config-table-cell">{settings?.extractor_model || formData.extractor_model || 'Not set'}</div>
                            </div>
                            <div className="config-table-row">
                                <div className="config-table-cell config-agent-name">Evaluator Agent</div>
                                <div className="config-table-cell">{settings?.evaluator_provider || formData.evaluator_provider || 'Not configured'}</div>
                                <div className="config-table-cell">{settings?.evaluator_model || formData.evaluator_model || 'Not set'}</div>
                            </div>
                            <div className="config-table-row">
                                <div className="config-table-cell config-agent-name">Summarizer Agent</div>
                                <div className="config-table-cell">{settings?.summarizer_provider || formData.summarizer_provider || 'Not configured'}</div>
                                <div className="config-table-cell">{settings?.summarizer_model || formData.summarizer_model || 'Not set'}</div>
                            </div>
                        </div>
                        
                        {/* Additional Settings */}
                        <div className="config-additional-settings">
                            <div className="config-row">
                                <span className="config-label">API Keys:</span>
                                <span className="config-value">
                                    {(() => {
                                        // Check if preferences exist and have providers configured
                                        // If provider is set, keys are available (either explicit key_id or default key)
                                        if (preferences) {
                                            const hasGlobalProvider = preferences.global?.provider;
                                            const hasExtractorProvider = preferences.agents?.extractor?.provider;
                                            const hasEvaluatorProvider = preferences.agents?.evaluator?.provider;
                                            const hasSummarizerProvider = preferences.agents?.summarizer?.provider;
                                            
                                            // Check if any provider is configured
                                            if (hasGlobalProvider || hasExtractorProvider || hasEvaluatorProvider || hasSummarizerProvider) {
                                                // Check if explicit keys are set
                                                const hasGlobalKey = preferences.global?.key_id;
                                                const hasExtractorKey = preferences.agents?.extractor?.key_id;
                                                const hasEvaluatorKey = preferences.agents?.evaluator?.key_id;
                                                const hasSummarizerKey = preferences.agents?.summarizer?.key_id;
                                                
                                                if (preferences.use_same_for_all && hasGlobalProvider) {
                                                    return hasGlobalKey ? '✓ Configured (Global Key)' : '✓ Configured (Using Default Key)';
                                                } else if (hasExtractorProvider || hasEvaluatorProvider || hasSummarizerProvider) {
                                                    const providerCount = [hasExtractorProvider, hasEvaluatorProvider, hasSummarizerProvider].filter(Boolean).length;
                                                    const keyCount = [hasExtractorKey, hasEvaluatorKey, hasSummarizerKey].filter(Boolean).length;
                                                    if (keyCount > 0) {
                                                        return `✓ Configured (${keyCount} explicit key${keyCount > 1 ? 's' : ''})`;
                                                    } else {
                                                        return `✓ Configured (${providerCount} provider${providerCount > 1 ? 's' : ''}, using default keys)`;
                                                    }
                                                }
                                            }
                                        }
                                        // Fallback to legacy check
                                        if (settings?.api_key || formData.api_key) {
                                            return settings?.api_key === '••••••••' ? '✓ Configured' : '✓ Configured';
                                        }
                                        // Check if any providers are configured (vendors exist)
                                        if (providers && providers.length > 0) {
                                            return '✓ Vendors configured (check preferences for key details)';
                                        }
                                        return 'Not set';
                                    })()}
                                </span>
                            </div>
                            {(settings?.base_url || formData.base_url) && (
                                <div className="config-row">
                                    <span className="config-label">Base URL:</span>
                                    <span className="config-value">{settings?.base_url || formData.base_url}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                <form onSubmit={handleSubmit} className="settings-form">
                    <div className="form-section form-section-editable">
                        <div className="model-preference-header">
                            <h2 className="form-title-editable">
                                <span className="form-icon">⚙️</span>
                                Update Model Preferences
                            </h2>
                            <label className="same-model-checkbox">
                                <input
                                    type="checkbox"
                                    checked={useSameModelForAll}
                                    onChange={handleUseSameModelChange}
                                />
                                <span>Check to use same model for all</span>
                            </label>
                        </div>
                        
                        {providers.length === 0 ? (
                            <div className="alert alert-error" style={{marginBottom: '1rem'}}>
                                No vendors have been added yet. Please use the "+ Add LLM" button to add a vendor first.
                            </div>
                        ) : (
                            <div className="model-preference-table">
                                {/* Extractor Agent Configuration */}
                                <div className="model-preference-row">
                                    <div className="model-preference-cell agent-label">
                                        Extractor Agent
                                    </div>
                                    <div className="model-preference-cell">
                                        <select
                                            id="extractor_provider"
                                            name="extractor_provider"
                                            value={formData.extractor_provider}
                                            onChange={handleInputChange}
                                            required
                                            className="model-preference-select"
                                        >
                                            {providers.map(provider => (
                                                <option key={provider.id} value={provider.id}>
                                                    {provider.name} {provider.note ? `(${provider.note})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div className="model-preference-cell">
                                        {formData.extractor_model === 'custom' || isCustomModel(formData.extractor_model) ? (
                                            <input
                                                type="text"
                                                id="extractor_model"
                                                name="extractor_model"
                                                value={formData.extractor_model === 'custom' ? (customModels.extractor || '') : formData.extractor_model}
                                                onChange={(e) => handleCustomModelInput(e, 'extractor_model')}
                                                placeholder="Enter custom model name"
                                                required
                                                className="model-preference-input"
                                            />
                                        ) : (
                                            <select
                                                id="extractor_model"
                                                name="extractor_model"
                                                value={formData.extractor_model}
                                                onChange={(e) => handleModelChange(e, 'extractor_model')}
                                                required
                                                className="model-preference-select"
                                                disabled={loadingModels[formData.extractor_provider]}
                                            >
                                                {loadingModels[formData.extractor_provider] ? (
                                                    <option value="">Loading models...</option>
                                                ) : (
                                                    getProviderModelsForAgent('extractor').map(model => (
                                                        <option key={model} value={model}>{model}</option>
                                                    ))
                                                )}
                                            </select>
                                        )}
                                    </div>
                                    
                                    <div className="model-preference-cell">
                                        <input
                                            type="number"
                                            id="extractor_temperature"
                                            name="extractor_temperature"
                                            value={formData.extractor_temperature}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="2"
                                            step="0.1"
                                            className="model-preference-input"
                                        />
                                    </div>
                                </div>
                                
                                {/* Evaluator Agent Configuration */}
                                <div className="model-preference-row">
                                    <div className="model-preference-cell agent-label">
                                        Evaluator Agent
                                    </div>
                                    <div className="model-preference-cell">
                                        <select
                                            id="evaluator_provider"
                                            name="evaluator_provider"
                                            value={formData.evaluator_provider}
                                            onChange={handleInputChange}
                                            required
                                            className="model-preference-select"
                                            disabled={useSameModelForAll}
                                        >
                                            {providers.map(provider => (
                                                <option key={provider.id} value={provider.id}>
                                                    {provider.name} {provider.note ? `(${provider.note})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div className="model-preference-cell">
                                        {formData.evaluator_model === 'custom' || isCustomModel(formData.evaluator_model) ? (
                                            <input
                                                type="text"
                                                id="evaluator_model"
                                                name="evaluator_model"
                                                value={formData.evaluator_model === 'custom' ? (customModels.evaluator || '') : formData.evaluator_model}
                                                onChange={(e) => handleCustomModelInput(e, 'evaluator_model')}
                                                placeholder="Enter custom model name"
                                                required
                                                className="model-preference-input"
                                                disabled={useSameModelForAll}
                                            />
                                        ) : (
                                            <select
                                                id="evaluator_model"
                                                name="evaluator_model"
                                                value={formData.evaluator_model}
                                                onChange={(e) => handleModelChange(e, 'evaluator_model')}
                                                required
                                                className="model-preference-select"
                                                disabled={useSameModelForAll || loadingModels[formData.evaluator_provider]}
                                            >
                                                {loadingModels[formData.evaluator_provider] ? (
                                                    <option value="">Loading models...</option>
                                                ) : (
                                                    getProviderModelsForAgent('evaluator').map(model => (
                                                        <option key={model} value={model}>{model}</option>
                                                    ))
                                                )}
                                            </select>
                                        )}
                                    </div>
                                    
                                    <div className="model-preference-cell">
                                        <input
                                            type="number"
                                            id="evaluator_temperature"
                                            name="evaluator_temperature"
                                            value={formData.evaluator_temperature}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="2"
                                            step="0.1"
                                            className="model-preference-input"
                                            disabled={useSameModelForAll}
                                        />
                                    </div>
                                </div>
                                
                                {/* Summarizer Agent Configuration */}
                                <div className="model-preference-row">
                                    <div className="model-preference-cell agent-label">
                                        Summarizer Agent
                                    </div>
                                    <div className="model-preference-cell">
                                        <select
                                            id="summarizer_provider"
                                            name="summarizer_provider"
                                            value={formData.summarizer_provider}
                                            onChange={handleInputChange}
                                            required
                                            className="model-preference-select"
                                            disabled={useSameModelForAll}
                                        >
                                            {providers.map(provider => (
                                                <option key={provider.id} value={provider.id}>
                                                    {provider.name} {provider.note ? `(${provider.note})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div className="model-preference-cell">
                                        {formData.summarizer_model === 'custom' || isCustomModel(formData.summarizer_model) ? (
                                            <input
                                                type="text"
                                                id="summarizer_model"
                                                name="summarizer_model"
                                                value={formData.summarizer_model === 'custom' ? (customModels.summarizer || '') : formData.summarizer_model}
                                                onChange={(e) => handleCustomModelInput(e, 'summarizer_model')}
                                                placeholder="Enter custom model name"
                                                required
                                                className="model-preference-input"
                                                disabled={useSameModelForAll}
                                            />
                                        ) : (
                                            <select
                                                id="summarizer_model"
                                                name="summarizer_model"
                                                value={formData.summarizer_model}
                                                onChange={(e) => handleModelChange(e, 'summarizer_model')}
                                                required
                                                className="model-preference-select"
                                                disabled={useSameModelForAll || loadingModels[formData.summarizer_provider]}
                                            >
                                                {loadingModels[formData.summarizer_provider] ? (
                                                    <option value="">Loading models...</option>
                                                ) : (
                                                    getProviderModelsForAgent('summarizer').map(model => (
                                                        <option key={model} value={model}>{model}</option>
                                                    ))
                                                )}
                                            </select>
                                        )}
                                    </div>
                                    
                                    <div className="model-preference-cell">
                                        <input
                                            type="number"
                                            id="summarizer_temperature"
                                            name="summarizer_temperature"
                                            value={formData.summarizer_temperature}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="2"
                                            step="0.1"
                                            className="model-preference-input"
                                            disabled={useSameModelForAll}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="form-actions">
                        <button
                            type="submit"
                            disabled={saving}
                            className="btn btn-primary"
                        >
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </button>
                    </div>
                </form>
                
                {/* Add LLM Modal */}
                <AddLLMModal
                    show={showAddLLMModal}
                    onClose={() => setShowAddLLMModal(false)}
                    providers={providers}
                    onSuccess={() => {
                        fetchSettings();
                        fetchProviders(); // Refresh vendor list to include newly added vendor
                        fetchCustomModels();
                        fetchPreferences(); // Refresh preferences to show updated key status
                    }}
                />
        </div>
    );
};

export default LLMSettingsPage;

